import { Fragment, useEffect, useRef, useState } from "react";
import { Container } from "react-bootstrap";
import { IChatHistory } from "../../models";
import { useMutation } from "react-query";
import { getChatTestResponse } from "../../api/agent";
import WordByWordDisplay from "./WordByWordDisplay";

type Props = {
  orgId: string;
  agentId: string;
  displayName: string;
  greeting: string;
  primaryColor: string;
  launcherImageURL: string;
  avatarImageURL: string;
  hidePoweredBy: boolean;
};

function ChatBox({
  orgId,
  agentId,
  displayName,
  greeting,
  primaryColor,
  launcherImageURL,
  avatarImageURL,
  hidePoweredBy
}: Props) {
  const [message, setMessage] = useState<string>("");
  const [newMessage, setNewMessage] = useState(false);
  const [chatHistory, setChatHistory] = useState<IChatHistory[]>([]);

  const chatBoxRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  //   chat mutation;
  const { mutateAsync: getChatResponseFn, isLoading } = useMutation(
    getChatTestResponse,
    {
      onSuccess: (data) => {
        setNewMessage(true);
        setChatHistory((prevChatHistory) => [...prevChatHistory, data]);
      },
      onError: (error) => {},
    }
  );
  const adjustHeight = () => {
    if (textareaRef.current) {
      //   textareaRef.current.style.height = "auto"; // Reset height to auto
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`; // Set height based on scrollHeight
    }
  };
  const handleSendMessage = () => {
    if (!message.trim()) return;

    // update chat History
    const newChatHistory = [...chatHistory, { content: message, role: "user" }];
    setChatHistory(newChatHistory);
    setMessage(""); // Clear the input field
    adjustHeight();

    // get http request
    getChatResponseFn({
      orgId,
      agentId,
      chatHistory: newChatHistory,
    });
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter") {
      if (e.shiftKey) return;
      e.preventDefault();
      handleSendMessage();
    }
  };
  useEffect(() => {
    if (greeting) {
      setChatHistory([{ role: "system", content: greeting }]);
      // setChatHistory([
      //   { role: "system", content: greeting },
      //   { role: "user", content: "I need help filling out the application. What official document do you require?" },
      //   { role: "assistant", content: "You can use a government-issued ID with your picture and date of birth, such as a passport, driver's license, or national ID card." }
      // ]);
    }
  }, [greeting]);

  useEffect(() => {
    if (chatBoxRef.current) {
      chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight; // Scroll to bottom
    }
  }, [chatHistory, isLoading]); // Depend on chatHistory and isLoading

  return (
    <Container style={{ position: "relative" }}>
      <div id="quickychat-container2" className="open2">
        <div id="quickychat-header2" style={{ backgroundColor: primaryColor }}>
          <div id="quickychat-header-left2">
            <img
              src={avatarImageURL || "/images/chat-icon.svg"}
              alt="Chatbot Avatar"
              className="quickychat-avatar2"
              style={{ backgroundColor: primaryColor }}
            />
            <span className="quickychat-title2">{displayName}</span>
          </div>
          <div id="quickychat-header-right2">
            <button className="quickychat-icon2" id="minimize-icon2">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <line
                  x1="5"
                  y1="12"
                  x2="19"
                  y2="12"
                  stroke="#FFFFFF"
                  strokeWidth="2"
                  strokeLinecap="round"></line>
              </svg>
            </button>
            <button
              className="quickychat-icon2"
              id="close-icon2"
              onClick={() =>
                setChatHistory([{ role: "system", content: greeting }])
              }>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <line
                  x1="6"
                  y1="6"
                  x2="18"
                  y2="18"
                  stroke="#FFFFFF"
                  strokeWidth="2"
                  strokeLinecap="round"></line>
                <line
                  x1="6"
                  y1="18"
                  x2="18"
                  y2="6"
                  stroke="#FFFFFF"
                  strokeWidth="2"
                  strokeLinecap="round"></line>
              </svg>
            </button>
          </div>
        </div>
        <div id="quickychat-messages2" ref={chatBoxRef}>
          {chatHistory.map((msg, index) => (
            <div
              key={index}
              className={`quickychat-message2  
							${msg.role}2`}>
              {msg.role !== "user" && (
                <img
                  src={avatarImageURL || "/images/chat-icon.svg"}
                  alt="Chatbot Avatar"
                  className="quickychat-message-avatar2"
                  style={{ backgroundColor: primaryColor }}
                />
              )}
              <span
                className="quickychat-message-content2"
                style={{
                  background: `${
                    msg.role === "user" ? primaryColor : "#e9ecef"
                  }`,
                }}>
                {index + 1 === chatHistory.length && newMessage ? (
                  <WordByWordDisplay text={msg.content} />
                ) : (
                  msg.content.split("\n").map((line, indx) => (
                    <Fragment key={indx}>
                      {line}
                      <br />
                    </Fragment>
                  ))
                )}
              </span>
            </div>
          ))}

          {isLoading && (
            <div className="quickychat-message2 assistant2 typing-indicator2">
              <img
                src={avatarImageURL || "/images/chat-icon.svg"}
                alt="Chatbot Avatar"
                className="quickychat-message-avatar2"
                style={{ backgroundColor: primaryColor }}
              />
              <span className="quickychat-message-content2 typing-dots2">
                <span></span>
                <span></span>
                <span></span>
              </span>
            </div>
          )}
        </div>
        <div id="quickychat-input-container2">
          <textarea
            value={message}
            ref={textareaRef}
            onKeyDown={handleKeyDown}
            onChange={(e) => setMessage(e.target.value)}
            onInput={adjustHeight} // auto-adjust height
            id="quickychat-input2"
            placeholder="Type a message..."></textarea>
        </div>
        {!hidePoweredBy &&
          <div id="quickychat-poweredby2">
            <p>
              Powered by <a href="https://quickychat.com/">QuickyChat.com</a>
            </p>
          </div>
        }
      </div>

      <button id="quickychat-trigger2">
        <img
          src={launcherImageURL || "/images/chat-icon.svg"}
          alt="Trigger Avatar"
          className="quickychat-avatar2"
          style={{ backgroundColor: primaryColor }}
        />
      </button>
    </Container>
  );
}

export default ChatBox;
